/**
 * Sidebar filter panel for the live menu — strain type, weight, THC potency,
 * brand. ALL four axes are multi-select checkbox style (per user request).
 * Brand has a searchable input on top because the list can be long.
 *
 * Options are dynamic: the parent only passes in the options for axes that
 * currently have at least one matching product (via the cross-axis facet
 * pipeline).
 */
import { useMemo, useState } from "react";
import { Search } from "lucide-react";

export const WEIGHTS = [
    { val: "0.5g", label: "0.5g" },
    { val: "1g", label: "1g" },
    { val: "2g", label: "2g" },
    { val: "3.5g", label: "1/8 oz (3.5g)" },
    { val: "7g", label: "1/4 oz (7g)" },
    { val: "14g", label: "1/2 oz (14g)" },
    { val: "28g", label: "1 oz (28g)" },
];

export const POTENCY = [
    { val: "0-10", label: "Under 10% THC" },
    { val: "10-20", label: "10–20% THC" },
    { val: "20-30", label: "20–30% THC" },
    { val: "30-100", label: "30%+ THC" },
];

export const STRAINS = [
    { val: "INDICA", label: "Indica" },
    { val: "SATIVA", label: "Sativa" },
    { val: "HYBRID", label: "Hybrid" },
    { val: "INDICA_HYBRID", label: "Indica-Hybrid" },
    { val: "SATIVA_HYBRID", label: "Sativa-Hybrid" },
    { val: "HIGH_CBD", label: "High CBD" },
];

/** Checkbox row — used by strain/weight/potency lists. */
function CheckboxRow({ checked, label, onToggle, count, testid }) {
    return (
        <li>
            <label
                data-testid={testid}
                className={`flex items-center gap-2 text-xs px-1.5 py-1.5 cursor-pointer transition-colors ${
                    checked
                        ? "bg-zen-olive/10 text-zen-ink font-medium"
                        : "text-zen-ink-soft hover:text-zen-ink hover:bg-zen-surface"
                }`}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    onChange={onToggle}
                    className="accent-zen-olive w-3.5 h-3.5"
                />
                <span className="flex-1 truncate">{label}</span>
                {count != null && (
                    <span className="text-[10px] text-zen-muted tabular-nums">
                        {count}
                    </span>
                )}
            </label>
        </li>
    );
}

export default function MenuFilters({
    // multi-select state — arrays from the parent
    selectedStrains = [],
    selectedBrands = [],
    selectedWeights = [],
    selectedPotency = [],
    // option lists (computed by the cross-axis facet pipeline)
    brandOptions = [],
    weightOptions = [],
    potencyOptions = [],
    strainOptions = [],
    onToggleStrain,
    onToggleBrand,
    onToggleWeight,
    onTogglePotency,
    onClearBrands,
}) {
    const [brandQuery, setBrandQuery] = useState("");
    const visibleBrands = useMemo(() => {
        const q = brandQuery.trim().toLowerCase();
        if (!q) return brandOptions;
        return brandOptions.filter((b) => b.name.toLowerCase().includes(q));
    }, [brandQuery, brandOptions]);

    return (
        <div className="space-y-7">
            {strainOptions.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                        Strain type
                    </p>
                    <ul className="space-y-0.5">
                        {strainOptions.map((s) => (
                            <CheckboxRow
                                key={s.val}
                                label={s.label}
                                checked={selectedStrains.includes(s.val)}
                                onToggle={() => onToggleStrain(s.val)}
                                testid={`filter-strain-${s.val.toLowerCase()}`}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {weightOptions.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                        Weight
                    </p>
                    <ul className="space-y-0.5">
                        {weightOptions.map((w) => (
                            <CheckboxRow
                                key={w.val}
                                label={w.label}
                                checked={selectedWeights.includes(w.val)}
                                onToggle={() => onToggleWeight(w.val)}
                                testid={`filter-weight-${w.val.toLowerCase()}`}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {potencyOptions.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive mb-3">
                        THC Potency
                    </p>
                    <ul className="space-y-0.5">
                        {potencyOptions.map((p) => (
                            <CheckboxRow
                                key={p.val}
                                label={p.label}
                                checked={selectedPotency.includes(p.val)}
                                onToggle={() => onTogglePotency(p.val)}
                                testid={`filter-potency-${p.val}`}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {brandOptions.length > 0 && (
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive">
                            Brand
                        </p>
                        {selectedBrands.length > 0 && (
                            <button
                                onClick={onClearBrands}
                                data-testid="filter-brand-clear"
                                className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft hover:text-zen-ink"
                            >
                                Clear ({selectedBrands.length})
                            </button>
                        )}
                    </div>
                    <div className="relative mb-2">
                        <Search
                            size={12}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zen-olive"
                        />
                        <input
                            value={brandQuery}
                            onChange={(e) => setBrandQuery(e.target.value)}
                            placeholder="Filter brands…"
                            data-testid="filter-brand-search"
                            className="w-full bg-white border border-zen-ink/15 hover:border-zen-ink/30 focus:border-zen-olive text-zen-ink text-xs placeholder:text-zen-muted pl-7 pr-2 py-2 focus:outline-none transition-colors"
                        />
                    </div>
                    <ul
                        data-testid="filter-brand-list"
                        className="space-y-0.5 max-h-72 overflow-y-auto pr-1"
                    >
                        {visibleBrands.map((b) => (
                            <CheckboxRow
                                key={b.name}
                                label={b.name}
                                checked={selectedBrands.includes(b.name)}
                                onToggle={() => onToggleBrand(b.name)}
                                count={b.count}
                                testid={`filter-brand-${b.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            />
                        ))}
                        {visibleBrands.length === 0 && (
                            <li className="text-xs text-zen-ink-soft px-2 py-2">
                                No brands match
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
